import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function AnimatedBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current; // Cache the ref value

    const width = window.innerWidth;
    const height = window.innerHeight;

    // Scene & Camera
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 400;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    // Star Geometry
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 1000;
    const starVertices = [];
    const starSizes = [];
    const starColors = [];

    for (let i = 0; i < starCount; i++) {
      starVertices.push(
        (Math.random() - 0.5) * 2000,
        (Math.random() - 0.5) * 2000,
        (Math.random() - 0.5) * 2000
      );
      starSizes.push(Math.random() * 1.5 + 0.5);
      starColors.push(
        0.5 + Math.random() * 0.5,
        0.5 + Math.random() * 0.5,
        0.8 + Math.random() * 0.2
      );
    }

    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
    starGeometry.setAttribute('size', new THREE.Float32BufferAttribute(starSizes, 1));
    starGeometry.setAttribute('color', new THREE.Float32BufferAttribute(starColors, 3));

    const starMaterial = new THREE.PointsMaterial({
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      size: 1.5,
    });

    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    // Nebula Background
    const textureLoader = new THREE.TextureLoader();
    const nebulaTexture = textureLoader.load('/textures/nebula.png');
    const nebulaMaterial = new THREE.SpriteMaterial({
      map: nebulaTexture,
      transparent: true,
      opacity: 0.15,
    });
    const nebulaSprite = new THREE.Sprite(nebulaMaterial);
    nebulaSprite.scale.set(1500, 1500, 1);
    scene.add(nebulaSprite);

    // Mouse Parallax
    const mouse = { x: 0, y: 0 };
    const onMouseMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', onMouseMove);

    // Animate
    const animate = () => {
      requestAnimationFrame(animate);
      stars.rotation.y += 0.0005;
      stars.rotation.x += 0.0002;
      stars.material.opacity = Math.random() * 0.3 + 0.5;
      camera.position.x += (mouse.x * 50 - camera.position.x) * 0.05;
      camera.position.y += (mouse.y * 50 - camera.position.y) * 0.05;
      camera.lookAt(scene.position);
      nebulaSprite.rotation.z += 0.0002;
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (mount && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return <div ref={mountRef} className="threejs-bg" style={{ position: 'fixed', top: 0, left: 0, zIndex: -1 }} />;
}
